import { useState } from 'react';
import { TouchableOpacity, View, Text } from 'react-native';
import { Tabs } from 'expo-router';
import DrawerMenu from '../../src/components/DrawerMenu';
import LegalUpdateBanner from '../../src/components/LegalUpdateBanner';
import SearchModal from '../../src/components/SearchModal';
import Icon, { ICON_SIZES } from '../../src/components/Icon';
import type { IconName } from '../../src/components/Icon';
import { useTheme } from '../../src/theme/ThemeContext';
import ConnectivityBanner from '../../src/components/ConnectivityBanner';
import { useNotificationStore } from '../../src/stores/notificationStore';

const TAB_ICONS: Record<string, { active: IconName; inactive: IconName }> = {
  activities: { active: 'checkbox', inactive: 'checkbox-outline' },
  goals: { active: 'flag', inactive: 'flag-outline' },
  visions: { active: 'eye', inactive: 'eye-outline' },
  'time-score': { active: 'stats-chart', inactive: 'stats-chart-outline' },
  settings: { active: 'settings', inactive: 'settings-outline' },
};

function NotificationsHeaderRight() {
  const { colors } = useTheme();
  const { unreadCount, markAllRead } = useNotificationStore();
  if (unreadCount === 0) return null;
  return (
    <TouchableOpacity
      onPress={markAllRead}
      style={{ paddingHorizontal: 12, minHeight: 44, justifyContent: 'center' }}
      accessibilityRole="button"
      accessibilityLabel="Mark all as read"
      testID="notifications.mark-all-read"
    >
      <Text style={{ fontSize: 13, fontWeight: '600', color: colors.primary }}>Mark All Read</Text>
    </TouchableOpacity>
  );
}

export default function TabsLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { colors } = useTheme();
  const notificationUnreadCount = useNotificationStore((s) => s.unreadCount);

  return (
    <>
      <ConnectivityBanner />
      <Tabs
        screenOptions={{
          tabBarActiveTintColor: colors.primary,
          tabBarInactiveTintColor: colors.textDisabled,
          // No explicit height/paddingBottom: react-navigation sizes the bar
          // from the bottom safe-area inset. Pinning it to 64pt put the labels
          // on top of the home indicator on every device that has one.
          tabBarStyle: {
            backgroundColor: colors.background,
            borderTopColor: colors.border,
            borderTopWidth: 1,
            paddingTop: 4,
          },
          tabBarLabelStyle: {
            fontSize: 13,
            fontWeight: '600',
          },
          headerStyle: {
            backgroundColor: colors.background,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.05,
            shadowRadius: 2,
            elevation: 2,
          },
          headerTintColor: colors.textPrimary,
          headerTitleStyle: {
            fontWeight: '700',
            fontSize: 18,
          },
          headerLeft: () => (
            <TouchableOpacity
              onPress={() => setDrawerOpen(true)}
              style={{ paddingHorizontal: 12, minHeight: 44, minWidth: 44, alignItems: 'center', justifyContent: 'center' }}
              accessibilityLabel="Open menu"
              accessibilityRole="button"
              testID="tabs.menu-button"
            >
              <View style={{ position: 'relative' }}>
                <Icon name="menu-outline" size={ICON_SIZES.header} color={colors.textSecondary} />
                {notificationUnreadCount > 0 && (
                  <View style={{ position: 'absolute', top: -4, right: -6, backgroundColor: colors.error, borderRadius: 6, width: 12, height: 12 }} />
                )}
              </View>
            </TouchableOpacity>
          ),
          headerRight: () => (
            <TouchableOpacity
              onPress={() => setSearchOpen(true)}
              style={{ paddingHorizontal: 12, minHeight: 44, minWidth: 44, alignItems: 'center', justifyContent: 'center' }}
              accessibilityLabel="Search"
              accessibilityRole="button"
              testID="tabs.search-button"
            >
              <Icon name="search-outline" size={ICON_SIZES.header} color={colors.textSecondary} />
            </TouchableOpacity>
          ),
        }}
      >
        <Tabs.Screen
          name="activities"
          options={{
            title: 'Activities',
            tabBarIcon: ({ focused }) => (
              <Icon name={focused ? TAB_ICONS.activities.active : TAB_ICONS.activities.inactive} size={ICON_SIZES.tabBar} color={focused ? colors.primary : colors.textDisabled} />
            ),
          }}
        />
        <Tabs.Screen
          name="goals"
          options={{
            title: 'Goals',
            tabBarIcon: ({ focused }) => (
              <Icon name={focused ? TAB_ICONS.goals.active : TAB_ICONS.goals.inactive} size={ICON_SIZES.tabBar} color={focused ? colors.primary : colors.textDisabled} />
            ),
          }}
        />
        <Tabs.Screen
          name="visions"
          options={{
            title: 'Visions',
            tabBarIcon: ({ focused }) => (
              <Icon name={focused ? TAB_ICONS.visions.active : TAB_ICONS.visions.inactive} size={ICON_SIZES.tabBar} color={focused ? colors.primary : colors.textDisabled} />
            ),
          }}
        />
        <Tabs.Screen
          name="time-score"
          options={{
            title: 'TimeScore\u2122',
            tabBarIcon: ({ focused }) => (
              <Icon name={focused ? TAB_ICONS['time-score'].active : TAB_ICONS['time-score'].inactive} size={ICON_SIZES.tabBar} color={focused ? colors.primary : colors.textDisabled} />
            ),
          }}
        />
        <Tabs.Screen
          name="notifications"
          options={{
            title: 'Notifications',
            href: null,
            headerRight: () => <NotificationsHeaderRight />,
          }}
        />
        <Tabs.Screen
          name="settings"
          options={{
            title: 'Settings',
            href: null,
          }}
        />
      </Tabs>

      <DrawerMenu
        visible={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
      <SearchModal
        visible={searchOpen}
        onClose={() => setSearchOpen(false)}
      />
      <LegalUpdateBanner />
    </>
  );
}
